import * as UI from "@iyk/ui"
import * as React from "react"

import * as ReactPortal from "@radix-ui/react-portal"

import "./playlist.css"

import { useAudioPlayer } from "./audio-player/useAudioPlayer.ts"
import { usePlaylistInteraction } from "./playlist-interaction/usePlaylistInteraction.tsx"
import { PlayPauseButton } from "./PlayPauseButton.tsx"

export function AudioPlayerBottomBar({ className }: { className?: string }) {
  const { currentTrack } = useAudioPlayer()
  const { setActivePlaylist } = usePlaylistInteraction()

  if (!currentTrack) return null

  return (
    <ReactPortal.Root
      id="audio-player-bottom-bar"
      className={UI.cx(
        "bg-background cursor-pointer",
        "sticky bottom-0 left-0 right-0 z-50",
        "animate-slide-up pointer-events-auto",
        "mx-auto w-full",
        className
      )}
      onClick={() => {
        setActivePlaylist(currentTrack.playlist.id)
      }}
    >
      <div className="max-w-[var(--app-max-width)] mx-auto">
        <SeekBar trackDuration={currentTrack.duration} />
        <div className="flex items-center gap-3 p-3">
          <div className="grid grid-cols-[auto_1fr] items-center gap-2 flex-1 overflow-hidden">
            <img
              src={currentTrack.playlist.coverImageUrl}
              alt={currentTrack.title}
              className="size-11 rounded-sm object-cover overflow-hidden shrink-0"
            />
            <div className="overflow-hidden flex-1">
              <p className="text-sm truncate text-ellipsis">{currentTrack.title}</p>
              <p className="text-xs text-gray-11 truncate text-ellipsis">{currentTrack.artist}</p>
            </div>
          </div>
          <div className="shrink-0">
            <Controls />
          </div>
        </div>
      </div>
    </ReactPortal.Root>
  )
}

function Controls() {
  const { trackIsPlaying, trackIsBuffering, playPreviousTrack, playNextTrack, togglePlayPause } =
    useAudioPlayer()

  return (
    <div className="flex items-center justify-center gap-3">
      <button
        className="flex items-center justify-center size-5 disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={(e) => {
          e.stopPropagation()
          playPreviousTrack()
        }}
      >
        <UI.Icon.SkipBack className="text-gray-12" />
      </button>
      <PlayPauseButton isPlaying={trackIsPlaying} isBuffering={trackIsBuffering} onClick={togglePlayPause} />
      <button
        className="flex items-center justify-center size-5 disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={(e) => {
          e.stopPropagation()
          playNextTrack()
        }}
      >
        <UI.Icon.SkipForward className="text-gray-12" />
      </button>
    </div>
  )
}

function SeekBar({ trackDuration }: { trackDuration: number }) {
  const { getCurrentTrackTime, updateCurrentTrackTime, trackIsReady } = useAudioPlayer()

  const [seek, setSeek] = React.useState<{ active: boolean; value: number }>({
    active: false,
    value: 0
  })

  const currentTime = getCurrentTrackTime()
  const value = seek.active ? seek.value : currentTime

  return (
    <UI.Slider.Root
      disabled={!trackIsReady}
      step={0.01}
      min={0}
      max={trackDuration}
      value={[value]}
      onValueChange={([value]) => {
        setSeek({ active: true, value })
      }}
      onValueCommit={([value]) => {
        updateCurrentTrackTime(value)
        setSeek({ active: false, value: 0 })
      }}
      onClick={(e) => {
        e.stopPropagation()
      }}
    >
      <UI.Slider.Track>
        <UI.Slider.Range />
      </UI.Slider.Track>
      <UI.Slider.Thumb />
    </UI.Slider.Root>
  )
}
